import React from 'react';
import {
  Button,
  FormField,
  Heading,
  Input,
  SkeletonGroup,
  SkeletonRectangle,
  Text,
  TextButton,
} from '@wix/design-system';
import { RefreshSmall, SparklesSmall } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  useStoreNameSuggestions,
  useSelectedCategory,
  useSelectedStoreName,
  useSetSelectedStoreName,
} from 'stores/appStore';
import { useSuggestStoreName } from '../../api';
import { AcceptTerms } from './components/AcceptTerms';
import styles from './StoreNameContent.scss';
import { StepLayout } from 'components/StepLayout/StepLayout';

export function StoreNameContent() {
  const { t } = useTranslation();
  const storeName = useSelectedStoreName() || '';
  const setStoreName = useSetSelectedStoreName();
  const suggestions = useStoreNameSuggestions();
  const category = useSelectedCategory();
  const { mutate: suggestStoreName, isPending: isSuggesting } = useSuggestStoreName();

  function handleGetSuggestions() {
    if (!category) {
      return;
    }
    suggestStoreName({
      previousResults: suggestions,
      language: window.__LOCALE__,
      products: [],
      category,
    });
  }

  function handleStoreNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setStoreName(e.target.value);
  }

  function handleSuggestionSelect(suggestion: string) {
    if (!isSuggesting) {
      setStoreName(suggestion);
    }
  }

  function renderSuggestions() {
    if (isSuggesting) {
      return (
        <div className={styles.suggestions}>
          <div className={styles.suggestionsHeader}>
            <Text size="small">{t('store_name.ai_suggestions_label')}</Text>
            <TextButton disabled prefixIcon={<RefreshSmall />} size="small">
              {t('store_name.get_suggestions_label')}
            </TextButton>
          </div>
          <SkeletonGroup>
            <div className={styles.suggestionsOptions}>
              {[...Array(5)].map((_, i) => (
                <SkeletonRectangle key={i} width="100px" height="32px" />
              ))}
            </div>
          </SkeletonGroup>
        </div>
      );
    }

    if (suggestions.length > 0) {
      return (
        <div className={styles.suggestions}>
          <div className={styles.suggestionsHeader}>
            <Text size="small">{t('store_name.ai_suggestions_label')}</Text>
            <TextButton
              disabled={!category}
              onClick={handleGetSuggestions}
              prefixIcon={<RefreshSmall />}
              size="small"
            >
              {t('store_name.get_suggestions_label')}
            </TextButton>
          </div>
          <div className={styles.suggestionsOptions}>
            {suggestions.map((suggestion) => (
              <Button
                key={suggestion}
                priority="secondary"
                size="small"
                skin="dark"
                prefixIcon={<SparklesSmall />}
                onClick={() => handleSuggestionSelect(suggestion)}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <StepLayout
      title={t('store_name.heading')}
      description={t('store_name.description')}
      disclaimer={t('store_name.ai_disclaimer')}
      nextButtonProps={{
        label: t('store_name.cta'),
        disabled: !storeName?.trim(),
      }}
    >
      <FormField>
        <Input
          value={storeName}
          onChange={handleStoreNameChange}
          placeholder={t('store_name.placeholder')}
          size="large"
        />
      </FormField>

      {renderSuggestions()}

      <div className={styles.acceptTermsSection}>
        <Heading size="large" className={styles.acceptTermsTitle}>
          {t('store_name_preview.products_fulfillment_title')}
        </Heading>

        <Text size="small" weight="normal">
          {t('store_name_preview.fulfillment_description')}
        </Text>

        <AcceptTerms />
      </div>
    </StepLayout>
  );
}
